import React from "react";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

interface CommunicationPreferencesSectionProps {
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  open: boolean;
  onToggle: () => void;
}

const NOTIFICATION_PREFS = [
  { key: "invoiceNotifications", label: "Invoice Notifications" },
  { key: "paymentReceipts", label: "Payment Receipts" },
  { key: "appointmentReminders", label: "Appointment Reminders" },
  { key: "matterUpdates", label: "Matter Updates" },
  { key: "documentRequests", label: "Document Requests" },
] as const;

const NOTIFICATION_OPTIONS = [
  { value: "", label: "Use firm default" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "both", label: "Both" },
  { value: "none", label: "None" },
];

const CHANNEL_OPTIONS = [
  { value: "", label: "Use firm default" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "phone", label: "Phone" },
  { value: "whatsapp", label: "WhatsApp" },
];

const GENERAL_OPTIONS = [
  { value: "", label: "Use firm default" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const selectClasses =
  "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors appearance-none cursor-pointer";

const CommunicationPreferencesSection: React.FC<CommunicationPreferencesSectionProps> = ({
  values,
  onChange,
  open,
  onToggle,
}) => {
  const updateField = (key: string, value: string) => {
    const next = { ...values };
    if (value === "") {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 w-full pt-2 mb-1 group"
      >
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider group-hover:text-slate-500 transition-colors">
          Communication Preferences
        </span>
        <span className="text-xs text-slate-300 font-normal normal-case">(optional)</span>
        <div className="flex-1 h-px bg-slate-100" />
        <ChevronDown
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="pt-3 space-y-4">
              {/* Notification preferences — 2-column grid */}
              <div className="grid grid-cols-2 gap-3">
                {NOTIFICATION_PREFS.map((pref) => (
                  <div key={pref.key}>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {pref.label}
                    </label>
                    <div className="relative">
                      <select
                        value={values[pref.key] || ""}
                        onChange={(e) => updateField(pref.key, e.target.value)}
                        className={selectClasses}
                      >
                        {NOTIFICATION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                ))}

                {/* Preferred Channel */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Preferred Channel
                  </label>
                  <div className="relative">
                    <select
                      value={values.preferredChannel || ""}
                      onChange={(e) => updateField("preferredChannel", e.target.value)}
                      className={selectClasses}
                    >
                      {CHANNEL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </div>

                {/* General Communications */}
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    General Communications
                  </label>
                  <div className="relative">
                    <select
                      value={values.generalCommunications || ""}
                      onChange={(e) => updateField("generalCommunications", e.target.value)}
                      className={selectClasses}
                    >
                      {GENERAL_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommunicationPreferencesSection;
